import type { FC, SVGProps } from 'react';
import { composeRenderProps, ListBoxItem as AriaListBoxItem, ListBoxItemProps } from 'react-aria-components';
import { NotAffectingLineHeightObject } from '../not-affecting-line-height-object';
import { Text } from '../typography';
import commonStyles from './Dropdowns.module.css';
import styles from './Option.module.css';

type Props = Pick<ListBoxItemProps, 'id' | 'href' | 'textValue' | 'children' | 'value' | 'isDisabled'> & {
  /**
   * A leading glyph, the same prop shape as `MenuItem`'s: the SVG component, not an element. It is
   * what makes a list of mixed things scannable — in the shell's search, whether a hit is a company,
   * a fund or a product is read from the icon before the name is read at all.
   */
  icon?: FC<SVGProps<SVGSVGElement>>;
};

export function Option({
  textValue: textValueFromProps,
  value,
  children: childrenFromProps,
  id,
  href,
  icon: Icon,
  isDisabled,
}: Props) {
  const textValue = textValueFromProps || (typeof childrenFromProps === 'string' ? childrenFromProps : undefined);
  return (
    <AriaListBoxItem
      id={id}
      value={value}
      href={href}
      textValue={textValue}
      isDisabled={isDisabled}
      className={commonStyles.item}
    >
      {composeRenderProps(childrenFromProps, children => {
        return (
          <>
            {Icon ? (
              <span className={styles.icon}>
                <NotAffectingLineHeightObject>
                  <Icon className={styles.svg} />
                </NotAffectingLineHeightObject>
              </span>
            ) : null}
            <div className={styles.text}>
              {typeof children === 'string' ? <Text slot="label">{children}</Text> : children}
            </div>
          </>
        );
      })}
    </AriaListBoxItem>
  );
}
